import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { CursoInfo, CursoMedio, CursosLista } from "../../../services/v2/prospects.service";

/**
 * Botón Cursos (Esteban, 2026-11-01): los cursos que se ofrecen hoy en el plantel, cada uno con su info breve (la misma para
 * todas y alineada con el router) y sus fotos y videos. La asesora elige el curso, ve cómo le llega y lo manda: primero el
 * texto y luego lo que deje marcado. Super Admin corrige el texto y sube o quita fotos y videos desde aquí.
 */

/** *negritas* y _cursivas_ de WhatsApp, como las ve el cliente. */
export const formatoWa = (texto: string): React.ReactNode[] =>
	texto.split("\n").map((linea, i) => {
		const partes: React.ReactNode[] = [];
		const re = /\*([^*\n]+)\*|_([^_\n]+)_/g;
		let ultimo = 0;
		let m: RegExpExecArray | null;
		while ((m = re.exec(linea))) {
			if (m.index > ultimo) partes.push(linea.slice(ultimo, m.index));
			partes.push(m[1] !== undefined ? <b key={m.index}>{m[1]}</b> : <i key={m.index}>{m[2]}</i>);
			ultimo = m.index + m[0].length;
		}
		if (ultimo < linea.length) partes.push(linea.slice(ultimo));
		return <React.Fragment key={i}>{partes}{i < texto.split("\n").length - 1 ? <br /> : null}</React.Fragment>;
	});

const urls = new Map<string, string>();

const Medio: React.FC<{ campusId: number; m: CursoMedio }> = ({ campusId, m }) => {
	const clave = m.id + (m.tipo === "foto" ? ":m" : ":v");
	const [url, setUrl] = useState<string | null>(urls.get(clave) || null);
	const [falla, setFalla] = useState(false);
	useEffect(() => {
		if (urls.get(clave)) { setUrl(urls.get(clave)!); return; }
		let vivo = true;
		HttpManager.getInstance().prospectsService.cursoMedio(campusId, m.id, m.tipo === "foto")
			.then((r) => { const u = URL.createObjectURL(r.data); urls.set(clave, u); if (vivo) setUrl(u); })
			.catch(() => { if (vivo) setFalla(true); });
		return () => { vivo = false; };
	}, [clave]);
	if (falla) return <div className="cm-medio-vacio">No se pudo abrir</div>;
	if (!url) return <div className="km-foto cargando" />;
	return m.tipo === "video" ? <video src={url} muted playsInline preload="metadata" /> : <img src={url} alt={m.nombre || "Foto del curso"} draggable={false} />;
};

const IcoPlay = () => <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M8 5.5v13l11-6.5z" /></svg>;

interface Props {
	campusId: number;
	prospectId: number;
	canalId: number | null;
	puedeEnviar: boolean;
	onEnviado: (avisoMatricula?: string | null) => void;
	onClose: () => void;
}

const CursosModal: React.FC<Props> = ({ campusId, prospectId, canalId, puedeEnviar, onEnviado, onClose }) => {
	const http = HttpManager.getInstance();
	const [datos, setDatos] = useState<CursosLista | null>(null);
	const [code, setCode] = useState<string | null>(null);
	const [marcados, setMarcados] = useState<number[]>([]);
	const [editando, setEditando] = useState<string | null>(null);
	const [subiendo, setSubiendo] = useState<number | null>(null);
	const [ocupado, setOcupado] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const archivo = useRef<HTMLInputElement>(null);

	const cargar = (elegir?: string | null) =>
		http.prospectsService.cursos(campusId, prospectId).then((r) => {
			setDatos(r.data);
			const c = elegir || r.data.sugerido || r.data.cursos[0]?.code || null;
			setCode(c);
			setMarcados((r.data.cursos.find((x) => x.code === c)?.medios || []).map((m) => m.id));
		}).catch((e) => setError(e?.response?.data?.message || "No se pudieron cargar los cursos."));
	useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campusId, prospectId]);

	const cursos = useMemo(() => {
		const l = datos?.cursos || [];
		const s = datos?.sugerido;
		return s ? [...l.filter((c) => c.code === s), ...l.filter((c) => c.code !== s)] : l;
	}, [datos]);
	const actual: CursoInfo | null = cursos.find((c) => c.code === code) || null;

	const elegir = (c: string) => {
		setCode(c); setEditando(null); setError(null);
		setMarcados((cursos.find((x) => x.code === c)?.medios || []).map((m) => m.id));
	};
	const alternar = (id: number) => setMarcados((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

	const enviar = async () => {
		if (!actual) return;
		setOcupado(true); setError(null);
		try {
			const ids = actual.medios.filter((m) => marcados.includes(m.id)).map((m) => m.id);
			const r = await http.prospectsService.enviarCurso(campusId, prospectId, actual.code, ids, canalId);
			onEnviado(r.data.avisoMatricula);
			if (r.data.error) setError(r.data.error); else onClose();
		} catch (e: any) { setError(e?.response?.data?.message || "No se pudo enviar."); }
		finally { setOcupado(false); }
	};
	const guardar = async () => {
		if (!actual || editando === null) return;
		setOcupado(true); setError(null);
		try { await http.prospectsService.cursoGuardar(campusId, actual.code, editando); setEditando(null); await cargar(actual.code); }
		catch (e: any) { setError(e?.response?.data?.message || "No se pudo guardar."); }
		finally { setOcupado(false); }
	};
	const subir = async (files: FileList | null) => {
		if (!actual || !files || !files.length) return;
		setError(null);
		try {
			for (const f of Array.from(files)) {
				setSubiendo(0);
				await http.prospectsService.cursoSubir(campusId, actual.code, f, setSubiendo);
			}
			await cargar(actual.code);
		} catch (e: any) { setError(e?.response?.data?.message || "No se pudo subir."); }
		finally { setSubiendo(null); if (archivo.current) archivo.current.value = ""; }
	};
	const quitar = async (m: CursoMedio) => {
		if (!actual || !window.confirm("¿Quitar este archivo del curso? Dejará de salir para todas las asesoras.")) return;
		try { await http.prospectsService.cursoQuitar(campusId, m.id); await cargar(actual.code); }
		catch (e: any) { setError(e?.response?.data?.message || "No se pudo quitar."); }
	};

	const nMedios = actual ? actual.medios.filter((m) => marcados.includes(m.id)).length : 0;
	const puedeEditar = !!datos?.puedeEditar;
	return (
		<Modal show onHide={ocupado ? undefined : onClose} size="xl" className="ventas-nuevo-modal km-modal cm-modal" fullscreen="md-down" scrollable>
			<Modal.Header closeButton={!ocupado}>
				<Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>Enviar Curso</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{error ? <div className="aviso error">{error}</div> : null}
				{!datos && !error ? <div className="apagado">Cargando…</div> : null}
				{datos && !cursos.length ? <div className="km-vacio">Este plantel todavía no tiene cursos con info para enviar.</div> : null}
				{cursos.length ? (
					<div className="km-cursos" role="tablist" aria-label="Curso">
						{cursos.map((c) => (
							<button key={c.code} type="button" role="tab" aria-selected={c.code === code} className={"km-chip" + (c.code === code ? " on" : "")} onClick={() => elegir(c.code)}>
								{c.nombre}
								{c.code === datos?.sugerido ? <span className="km-interes">Le interesa</span> : null}
							</button>
						))}
					</div>
				) : null}
				{actual ? (
					<div className="cm-cuerpo">
						<div className="cm-chat">
							{editando !== null ? (
								<textarea className="cm-editar" value={editando} onChange={(e) => setEditando(e.target.value)} rows={12} aria-label="Módulos y metodología del curso"
									placeholder={"🎓 *Módulos:*\n• *Módulo 1:* …\n✨ *Curso 100% Práctico* de 4 horas a la semana"} />
							) : (
								<div className="cm-burbuja">{formatoWa(actual.texto)}</div>
							)}
							{puedeEditar ? (
								<div className="cm-editar-pie">
									{editando !== null ? (
										<>
											<span className="cm-ayuda">Módulos y metodología. Título, duración, mensualidad e inicios salen solos de la Oferta Académica.</span>
											<button className="btn-wa" disabled={ocupado} onClick={() => setEditando(null)}>Cancelar</button>
											<button className="btn-wa pri" disabled={ocupado} onClick={guardar}>{ocupado ? "Guardando…" : "Guardar"}</button>
										</>
									) : <button className="btn-wa cv-chico" onClick={() => setEditando(actual.breve)}>Editar módulos</button>}
								</div>
							) : null}
						</div>
						{actual.medios.length || puedeEditar ? (
							<div className="cm-medios">
								<div className="cm-medios-titulo">Fotos y videos</div>
								<div className="cm-medios-grid">
									{actual.medios.map((m) => {
										const on = marcados.includes(m.id);
										return (
											<div key={m.id} className={"cm-medio" + (on ? " on" : "")}>
												<button type="button" className="cm-medio-tocar" onClick={() => alternar(m.id)} aria-pressed={on} aria-label={(on ? "Quitar " : "Elegir ") + (m.nombre || m.tipo)}>
													<Medio campusId={campusId} m={m} />
													{m.tipo === "video" ? <span className="cm-play"><IcoPlay /></span> : null}
													<span className="hm-check km-check" aria-hidden="true">{on ? "✓" : ""}</span>
												</button>
												{puedeEditar ? <button type="button" className="cm-quitar" onClick={() => quitar(m)} aria-label="Quitar del curso" title="Quitar del curso">×</button> : null}
											</div>
										);
									})}
									{puedeEditar ? (
										<button type="button" className="cm-agregar" disabled={subiendo !== null} onClick={() => archivo.current?.click()}>
											{subiendo !== null ? "Subiendo " + Math.round(subiendo * 100) + "%" : <><span aria-hidden="true">+</span>Foto o video</>}
										</button>
									) : null}
								</div>
								<input ref={archivo} type="file" hidden multiple accept="image/*,video/*,.heic,.mov" onChange={(e) => subir(e.target.files)} />
							</div>
						) : null}
					</div>
				) : null}
			</Modal.Body>
			<Modal.Footer className="hm-pie">
				<button className="btn-wa pri hm-enviar" disabled={!actual || !puedeEnviar || ocupado || editando !== null} onClick={enviar}
					title={puedeEnviar ? "" : "Pasaron 24 h desde su último mensaje: solo plantilla"}>
					{ocupado ? "Enviando…" : nMedios ? "Enviar info y " + nMedios + (nMedios > 1 ? " archivos" : " archivo") : "Enviar info"}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default CursosModal;
